import { useAddressFilterChoices } from './useAddressFilterChoices';
import { filterAddressOption } from '@/core/utils/address-search';
import { Select, DatePicker, ConfigProvider, Input, InputNumber, Button } from "antd";
import { MutableRefObject, useRef, ReactNode, SetStateAction, Dispatch, useEffect } from "react";
import { FilterSearchPayload } from "../table/DataTable";
import { debounce } from "ts-debounce";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import dayjs, { Dayjs } from "dayjs";

import 'dayjs/locale/vi';
import { useSavedFilters } from "@/core/stores/filter.store";
import { AddressRepo } from "@/core/repos/address.repo";
import container from "@/lib/di/container";
import { CloseCircleFilled, CloseCircleOutlined } from "@ant-design/icons";
import { useLocation } from "react-router";
import { DEF_DATE_FORMAT, DEF_DATETIME_FORMAT, DEF_MONTH_FORMAT } from "../../utils/global.utils";
dayjs.locale('vi');

const addressRepo = container.get(AddressRepo);

export enum FilterInputType {
    text, select, date, dateRange, number, element, percentage, month
}
export interface FilterOptions {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
}
export interface FilterInputTemplate {
    type: FilterInputType,
    dataIndex?: string,
    monthIndex?: {month: string, year: string},
    rangeIndex?: {from: string, to: string}
    showTime?: boolean
    placeholder?: string,
    secondaryPlaceholder?: string,
    label?: string,
    options?: FilterOptions[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValue?: any
    element?: ReactNode
    span?: number
    disabled?: boolean
    inputDisabled?: boolean
    loading?: boolean
}
interface FilterBarProps {
    map: Map<string, FilterSearchPayload>,
    setMap: Dispatch<SetStateAction<Map<string, FilterSearchPayload>>>,
    templates: FilterInputTemplate[],
    onChange: (map: Map<string, FilterSearchPayload>, changes: string[])=>void,
    actions?: ReactNode[],
    hideResetButton?: boolean,
    noCache?: boolean,
    /** Chỉ số, khách hàng, hóa đơn: xã phải có khu vực của nhà máy. Form khác vẫn lấy đủ xã theo tỉnh. */
    factoryScopedWards?: boolean
}

interface InternalFilterBarProps {
    map: Map<string, FilterSearchPayload>,
    templates: FilterInputTemplate[],
    onChange: (map: Map<string, FilterSearchPayload>, changes: string[])=>void
    actions: ReactNode[]
    hideResetButton?: boolean
}

export const FilterBarInternal: React.FC<InternalFilterBarProps> = ({onChange, templates, map, actions, hideResetButton}) => {
    const searchMap: MutableRefObject<Map<string, FilterSearchPayload>> = useRef(new Map(map));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleChange(value: any, name: string) {
        const next = new Map(searchMap.current);
        if (value === undefined || value === null || value === '') next.delete(name);
        else next.set(name, {key: name, value});
        if (name === 'provinceId') {
            next.delete('districtId');
            next.delete('wardId');
        }
        searchMap.current = next;
        onChange(new Map(next), []);
    }

    useEffect(() => {
        searchMap.current = new Map(map);
    }, [map])

    function handleMonthChange(index: {month: string, year: string}, value: Dayjs) {
        if (value&&value.isValid()) {
            searchMap.current.set(index.month, {key: index.month, value: (value.month()+1).toString()});
            searchMap.current.set(index.year, {key: index.year, value: value.year().toString()});
        } else {
            searchMap.current.delete(index.month);
            searchMap.current.delete(index.year);
        }
        onChange(searchMap.current, []);
    }

    function handleRangeChange(index: {from: string, to: string}, dates: [Dayjs | null, Dayjs | null] | null) {
        if (!dates?.[0]?.isValid() || !dates?.[1]?.isValid()) {
            searchMap.current.delete(index.from);
            searchMap.current.delete(index.to);
            onChange(searchMap.current, []);
            return;
        }

        // Chuẩn ISO 8601 UTC ("...Z") — trước đây gắn nhầm "+00:00" vào giờ local gây lệch múi giờ
        searchMap.current.set(index.from, {key: index.from, value: dates[0].toISOString()});
        searchMap.current.set(index.to, {key: index.to, value: dates[1].toISOString()});
        onChange(searchMap.current, []);
    }

    function parseMonthValue(index: {month: string, year: string}) {
        const month = searchMap.current.get(index.month);
        const year = searchMap.current.get(index.year);
        if (!month||!year) return null;
        return dayjs(`${year.value}-${month.value}-01`);
    }

    function resetFilters() {
        onChange(new Map(), []);
    }

    function getRangeValue(template: FilterInputTemplate): [Dayjs | null, Dayjs | null] {
        const fromString = map.get(template.rangeIndex?.from??"from")?.value;
        const toString = map.get(template.rangeIndex?.to??"to")?.value;
        // Parse trực tiếp ISO (Z / ±offset) hoặc cache cũ — không được split("+") rồi gắn lại TZ
        const from = fromString ? dayjs(fromString) : null;
        const to = toString ? dayjs(toString) : null;
        return [
            from?.isValid() ? from : null,
            to?.isValid() ? to : null,
        ];
    }

    function getTemplateKey(template: FilterInputTemplate, index: number): string {
        if (template.dataIndex) {
            return template.dataIndex;
        }
        if (template.rangeIndex) {
            return `${template.rangeIndex.from}-${template.rangeIndex.to}`;
        }
        if (template.monthIndex) {
            return `${template.monthIndex.month}-${template.monthIndex.year}`;
        }
        // Fallback to index if no unique identifier is available
        return `filter-${index}`;
    }

    function getElement(template: FilterInputTemplate) {
        switch(template.type) {
            case FilterInputType.text:
                return <Input key={template.dataIndex} placeholder={template.placeholder} value={map.get(template?.dataIndex??"")?.value??""} onChange={(e) => handleChange(e.target.value, template?.dataIndex??"")}/>
            case FilterInputType.select:
                return <Select key={template.dataIndex} showSearch allowClear disabled={template.inputDisabled} loading={template.loading}
                options={(template.options??[]).map(opt => ({value: opt.value, label: opt.name}))}
                filterOption={['provinceId', 'districtId', 'wardId'].includes(template.dataIndex ?? '') ? filterAddressOption : (input, option) =>
                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                className="w-full" placeholder={template.placeholder} onChange={(value)=>handleChange(value, template?.dataIndex??"")} value={map.get(template?.dataIndex??"")?.value??null}>
                    <Select.Option value={null}>Tất cả</Select.Option>
                    {template.options?template.options.map(option => (
                        <Select.Option key={option.value} value={option.value}>{option.name}</Select.Option>
                    )):null}
                </Select>
            case FilterInputType.date:
                return <DatePicker format={DEF_DATE_FORMAT} key={template.dataIndex}  placeholder={template.placeholder} onChange={(_, date)=> handleChange(date, template?.dataIndex??"")}/>
            case FilterInputType.dateRange:
                return <DatePicker.RangePicker
                    format={template.showTime?DEF_DATETIME_FORMAT:DEF_DATE_FORMAT}
                    showTime={template.showTime}
                    value={getRangeValue(template)}
                    style={{width: "100%"}}
                    allowClear
                    key={`${template.rangeIndex?.from ?? "from"}-${template.rangeIndex?.to ?? "to"}`}
                    placeholder={[template?.placeholder??"", template?.secondaryPlaceholder??template.placeholder??""]}
                    onChange={(dates)=> handleRangeChange(template.rangeIndex??{from: "from", to: "to"}, dates)}/>
            case FilterInputType.number:
                return <Input key={template.dataIndex} type="number" placeholder={template.placeholder} value={map.get(template?.dataIndex??"")?.value??""} onChange={(e) => handleChange(e.target.value, template?.dataIndex??"")}/>
            case FilterInputType.percentage:
                    return <InputNumber<number>
                    key={template.dataIndex}
                    placeholder={template.placeholder}
                    min={0}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value?.replace('%', '') as unknown as number}
                    onChange={(e) => handleChange((e??0).toString(), template?.dataIndex??"")}
                  />   
            case FilterInputType.element:
                return template.element     
            case FilterInputType.month:
                return <DatePicker.MonthPicker
                    format={DEF_MONTH_FORMAT}
                    key={`${template.monthIndex?.month ?? "month"}-${template.monthIndex?.year ?? "year"}`}
                    className="w-full"
                    placeholder={template.placeholder}
                    onChange={(e) => handleMonthChange(template.monthIndex??{month: "month", year: "year"}, e)}
                    value={parseMonthValue(template.monthIndex??{month: "month", year: "year"})}
                />
        }
    }
    return (
        <div className="text-xs">
            <div className="grid grid-cols-4 gap-x-4 gap-y-6 items-end mt-1 pb-2">
                {templates.filter(t => t.disabled !== true && t.dataIndex !== 'districtId').map((template, index) => (
                <div key={getTemplateKey(template, index)} className={`w-full flex flex-col items-start${template.span?" col-span-" + template.span:""}`}>
                    {template.label&&<p className="mb-2 text-[16px] font-[400]">{template.label}</p>}
                    {getElement(template)}
                </div>))}
                {actions}
            </div>
            <div className="flex flex-row justify-start mt-1">
                {!hideResetButton&&<Button
                    type="text"
                    onClick={resetFilters}
                    icon={<CloseCircleFilled />}
                    size="small"
                    style={{
                        fontSize: 13,
                        color: "gray"}}>Xoá bộ lọc</Button>}
            </div>
        </div>
    )
}

const cloneFilters = (map: Map<string, FilterSearchPayload>) => new Map(
    Array.from(map, ([key, payload]) => [key, { ...payload }] as const)
);

export const FilterBar: React.FC<FilterBarProps> = ({onChange, templates, map, setMap, actions, hideResetButton, noCache, factoryScopedWards}) => {
    const oldSearchMap = useRef(cloneFilters(map));
    const restoreVersion = useRef(0);
    const {pathname} = useLocation();
    const savedFilterStore = useSavedFilters();
    const hasAddress = templates.some(template => ['provinceId', 'districtId', 'wardId'].includes(template.dataIndex || ''));
    const provinceId = map.get('provinceId')?.value;
    const address = useAddressFilterChoices(addressRepo, hasAddress, provinceId, map.get('wardId')?.value, factoryScopedWards);

    const handleChange = (incoming: Map<string, FilterSearchPayload>, _changes: string[], restoring = false) => {
        restoreVersion.current++;
        const next = cloneFilters(incoming);
        next.delete('districtId');
        const changedFields = Array.from(new Set([...Array.from(oldSearchMap.current.keys()), ...Array.from(next.keys())]))
            .filter(key => oldSearchMap.current.get(key)?.value !== next.get(key)?.value);
        // Recovering a province for an existing ward is hydration, not a user change.
        if (restoring && next.get('wardId')?.value && !changedFields.includes('wardId')) changedFields.push('wardId');
        if (changedFields.includes('provinceId') && !changedFields.includes('wardId')) next.delete('wardId');
        oldSearchMap.current = cloneFilters(next);
        if (!noCache) savedFilterStore.setPageFilter(pathname, next);
        setMap(cloneFilters(next));
        onChange(cloneFilters(next), changedFields);
    };

    useEffect(() => {
        const version = ++restoreVersion.current;
        const saved = !noCache ? savedFilterStore.savedFilter.get(pathname) : undefined;
        const next = cloneFilters(saved || map);
        const restore = async () => {
            // Old cached district-only/ward-only filters must first recover their province.
            if (!next.get('provinceId')?.value) {
                const wardId = next.get('wardId')?.value;
                const districtId = next.get('districtId')?.value;
                const province = wardId ? (await addressRepo.getWard(wardId))?.provinceId
                    : districtId ? (await addressRepo.getDistrict(districtId))?.provinceId : undefined;
                if (province) next.set('provinceId', { key: 'provinceId', value: province });
            }
            if (version !== restoreVersion.current) return;
            if (saved || next.has('districtId') || next.get('provinceId')?.value !== map.get('provinceId')?.value) {
                handleChange(next, [], true);
            }
        };
        void restore();
        return () => { restoreVersion.current++; };
        // Restoration happens once per page. Explicit changes invalidate pending migration.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, noCache]);

    const addressTemplates = templates.map(template => {
        if (template.dataIndex === 'provinceId') return { ...template, options: address.provinces };
        if (template.dataIndex === 'wardId') return {
            ...template, options: address.wards, inputDisabled: !provinceId, loading: address.loadingWards,
        };
        return template;
    });
    return <ConfigProvider theme={MODAL_THEME}>
        <FilterBarInternal onChange={handleChange} templates={addressTemplates} map={map} actions={actions??[]} hideResetButton={hideResetButton} />
    </ConfigProvider>;
};

// Retain the page/report callback contract; address options are owned by FilterBar.
// This helper deliberately performs no asynchronous lookup or template writes.
export const handleAddressChange = async (
    map: Map<string, FilterSearchPayload>,
    changes: string[],
    filterTemplates: FilterInputTemplate[],
    _setFilterTemplates?: Dispatch<SetStateAction<FilterInputTemplate[]>>,
) => {
    map.delete('districtId');
    if (changes.includes('provinceId') && !changes.includes('wardId')) map.delete('wardId');
    return undefined;
};
